import { CompanionsTableCellContent } from "@/types/CompanionsTableCellContent";
import React from "react";
import { PGuestDetails } from "../../PGuestDetails/PGuestDetails";
import { useParams } from "react-router-dom";
import { CoveringLoadingSpinner } from "../../LoadingSpinner/CoveringLoadingSpinner";
import { GuestType } from "@/types/enums/GuestType";
import { PError } from "../../PError/PError";
import { useCompanions } from "./hooks/useCompanions";
import { AnimatePresence } from "framer-motion";
import { useRole } from "@/providers/RoleProvider";
import { useGuestsOptional } from "@/providers/GuestsProvider";
import { useGuestCompanionsOptional } from "@/providers/GuestCompanions";
import { IconUser } from "@tabler/icons-react";

type TableCellContentCompanionsProps = {
  isEditing: boolean;
  companions: CompanionsTableCellContent;
};

export const TableCellContentCompanions: React.FC<
  TableCellContentCompanionsProps
> = ({ isEditing, companions }) => {
  const params = useParams();
  const eventId = params.eventId;
  const { legacyRoleFloor, role } = useRole();
  /**
   * The popup needs BOTH contexts: `GuestDetailBody` reads `useGuests` and
   * `useGuestCompanions` at its top level, and both hard-throw without their
   * provider. This cell renders wherever a COMPANIONS column does, including
   * surfaces that provide neither (the sent-mails page, via a saved view) — a
   * chip click there took the whole page down.
   *
   * Withheld rather than degraded: a surface with no guests context cannot save
   * a guest, and a popup whose Save silently does nothing is worse than no
   * popup. The chips themselves keep rendering, so no information is lost.
   */
  // ⚠ Both hooks called UNCONDITIONALLY, then combined. `a() && b()` short-
  // circuits, so the second hook would only run on renders where the first
  // returned a context — a hooks-order violation that breaks on the render
  // after the provider appears.
  const guestsCtx = useGuestsOptional();
  const companionsCtx = useGuestCompanionsOptional();
  const canOpenPopup = !!guestsCtx && !!companionsCtx;

  const {
    processCompanionClick,
    errorMessage,
    loading,
    guestDetails,
    setErrorMessage,
    setGuestDetails,
  } = useCompanions({ eventId: eventId as string });

  // For companions, show the linked main guest; for main guests, show companions list.
  // The main guest arrives either as `mainGuestDTO` (guest-list $lookup path) or as the
  // single entry in `companions` (requests/Begleitung path), so accept both shapes.
  const isCompanion = companions?.guestType === GuestType.COMPANION;
  const displayList = isCompanion
    ? companions?.mainGuestDTO
      ? [companions.mainGuestDTO]
      : (companions?.companions ?? [])
    : (companions?.companions ?? []);

  // No click handler on the wrapper below: only the chips own their clicks
  // (they stop propagation in processCompanionClick). A blanket
  // stopPropagation on the wrapper made the whole Association cell a dead
  // zone — on the requests page, clicking the cell around a chip must bubble
  // to the row like every other cell so the row's detail popup opens.
  return (
    <>
      {isEditing ? (
        <></>
      ) : (
        <div className="flex flex-row">
          {displayList.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {displayList.map((item, index) => (
                <span
                  key={item.guestId || index}
                  onClick={async (e) => {
                    if (legacyRoleFloor === "VIEWER") return;
                    // See `canOpenPopup`: no guest context ⇒ no popup, and no
                    // fetch for one either.
                    if (!canOpenPopup) return;
                    await processCompanionClick(item.guestId || "", e);
                  }}
                  // Inert BOTH ways it can be inert: a VIEWER may not open the popup,
                  // and neither may a surface that provides no guest context
                  // (`canOpenPopup`). The second half used to advertise itself —
                  // pointer cursor, hover lift — while the handler returned early
                  // at `!canOpenPopup`, i.e. BEFORE `processCompanionClick`, which
                  // owns the only `stopPropagation` in the chain. The click then
                  // bubbled to the row and opened whatever that row opens: on the
                  // requests lane the registrant instead of the companion, on the
                  // sent-mails lane the email. `pointer-events-none` closes both
                  // the false affordance and the wrong-target click at once.
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md surface-tertiary border border-transparent body-3-medium font-secondary transition-all ${legacyRoleFloor === "VIEWER" || !canOpenPopup ? "pointer-events-none" : "cursor-pointer hover:surface-quaternary hover:border hover:outline-tertiary hover:shadow-sm"}`}
                >
                  <IconUser size={12} />
                  {item.firstName && item.lastName
                    ? `${item.firstName} ${item.lastName}`
                    : item.firstName || item.lastName || "-"}
                </span>
              ))}
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
      {loading && <CoveringLoadingSpinner />}
      <AnimatePresence>
        {errorMessage && (
          <PError
            onClose={() => setErrorMessage(null)}
            messages={[errorMessage]}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {guestDetails.open && (
          <PGuestDetails
            onCloseAction={() => setGuestDetails({ open: false })}
            guest={guestDetails.guest!}
            avoidStateUpdate={true}
          />
        )}
      </AnimatePresence>
    </>
  );
};
